import { createGlobalStyle } from 'styled-components';
import { media} from './Responsive';

const GlobalStyles = createGlobalStyle`
  :root {
    /* Light mode variables */
    --primary: #223460;
    --primary-dark: #003d7a;
    --secondary: #d28660;
    --light: #f8f9fa;
    --dark: #212529;
    --gray: #6c757d;
    --success: #28a745;
    --danger: #dc3545;
    --white: #ffffff;
    --black: #000000;
    --bg-color: var(--light);
    --text-color: var(--dark);
    --card-bg: var(--white);
    --border-color: #e9ecef;
    --input-bg: var(--white);
    --input-text: var(--dark);
    --shadow-color: rgba(0, 0, 0, 0.1);
    --transition: all 0.3s ease-in-out;
    --shadow: 0 4px 6px var(--shadow-color);
    --shadow-lg: 0 10px 15px var(--shadow-color);
    --border-radius: 8px;
    
    /* Spacing variables for consistent layout */
    --spacing-xs: 0.25rem;
    --spacing-sm: 0.5rem;
    --spacing-md: 1rem;
    --spacing-lg: 1.5rem;
    --spacing-xl: 2rem;
    --spacing-xxl: 3rem;
    
    /* Container max widths */
    --container-sm: 540px;
    --container-md: 720px;
    --container-lg: 960px;
    --container-xl: 1140px;
    --container-xxl: 1320px;
  }

  /* Dark mode variables */
  body.dark-mode {
    --bg-color: #121212;
    --text-color: #f8f9fa;
    --card-bg: #1e1e1e;
    --border-color: #2d2d2d;
    --input-bg: #2d2d2d;
    --input-text: #f8f9fa;
    --shadow-color: rgba(0, 0, 0, 0.3);
    --gray: #a0a0a0;
    --dark-bg: #121212;
    --dark-bg-secondary: #1e1e1e;
    --dark-text-color: #f8f9fa;
  }

  * {
    margin: 0;
    padding: 0;
    box-sizing: border-box;
  }

  body {
    font-family: 'Roboto', sans-serif;
    background-color: var(--bg-color);
    color: var(--text-color);
    overflow-x: hidden;
    font-size: 16px;
    line-height: 1.6;
    transition: background-color 0.3s ease, color 0.3s ease;
    
    ${media.down.md`
      font-size: 15px;
    `}
    
    ${media.down.sm`
      font-size: 14px;
    `}
  }

  h1, h2, h3, h4, h5, h6 {
    font-weight: 700;
    margin-bottom: 1rem;
    line-height: 1.2;
    color: var(--text-color);
  }
  
  h1 {
    font-size: 2.5rem;
    
    ${media.down.lg`
      font-size: 2.25rem;
    `}
    
    ${media.down.md`
      font-size: 2rem;
    `}
    
    ${media.down.sm`
      font-size: 1.75rem;
    `}
  }
  
  h2 {
    font-size: 2rem;
    
    ${media.down.lg`
      font-size: 1.75rem;
    `}
    
    ${media.down.md`
      font-size: 1.5rem;
    `}
    
    ${media.down.sm`
      font-size: 1.35rem;
    `}
  }
  
  h3 {
    font-size: 1.75rem;
    
    ${media.down.lg`
      font-size: 1.5rem;
    `}
    
    ${media.down.md`
      font-size: 1.35rem;
    `}
    
    ${media.down.sm`
      font-size: 1.25rem;
    `}
  }
  
  h4 {
    font-size: 1.5rem;
    
    ${media.down.md`
      font-size: 1.35rem;
    `}
    
    ${media.down.sm`
      font-size: 1.2rem;
    `}
  }

  p {
    margin-bottom: 1rem;
    color: var(--text-color);
  }

  a {
    color: var(--primary);
    text-decoration: none;
    transition: var(--transition);
    
    &:hover {
      color: var(--primary-dark);
    }
  }

  img {
    max-width: 100%;
    height: auto;
  }

  button, .btn {
    cursor: pointer;
    border: none;
    outline: none;
    background-color: var(--primary);
    color: var(--white);
    padding: 0.75rem 1.5rem;
    border-radius: var(--border-radius);
    transition: var(--transition);
    font-weight: 500;
    display: inline-block;
    text-align: center;
    
    &:hover {
      background-color: var(--primary-dark);
      transform: translateY(-2px);
    }
    
    &:disabled {
      opacity: 0.7;
      cursor: not-allowed;
    }
    
    ${media.down.sm`
      padding: 0.6rem 1.2rem;
      font-size: 0.9rem;
    `}
  }

  .container {
    width: 100%;
    max-width: 1200px;
    margin: 0 auto;
    padding: 0 15px;
    
    ${media.down.xl`
      max-width: var(--container-xl);
    `}
    
    ${media.down.lg`
      max-width: var(--container-lg);
    `}
    
    ${media.down.md`
      max-width: var(--container-md);
    `}
    
    ${media.down.sm`
      max-width: var(--container-sm);
      padding: 0 10px;
    `}
  }

  section {
    padding: 80px 0;
    
    ${media.down.lg`
      padding: 70px 0;
    `}
    
    ${media.down.md`
      padding: 60px 0;
    `}
    
    ${media.down.sm`
      padding: 50px 0;
    `}
  }
  
  .row {
    display: flex;
    flex-wrap: wrap;
    margin: 0 -15px;
    
    ${media.down.sm`
      margin: 0 -10px;
    `}
  }
  
  .col {
    flex: 1;
    padding: 0 15px;
    
    ${media.down.sm`
      padding: 0 10px;
    `}
  }
  
  /* Responsive grid system */
  .grid {
    display: grid;
    gap: 30px;
    
    ${media.down.md`
      gap: 20px;
    `}
    
    ${media.down.sm`
      gap: 15px;
    `}
  }
  
  .grid-2 {
    grid-template-columns: repeat(2, 1fr);
    
    ${media.down.sm`
      grid-template-columns: 1fr;
    `}
  }
  
  .grid-3 {
    grid-template-columns: repeat(3, 1fr);
    
    ${media.down.md`
      grid-template-columns: repeat(2, 1fr);
    `}
    
    ${media.down.sm`
      grid-template-columns: 1fr;
    `}
  }
  
  .grid-4 {
    grid-template-columns: repeat(4, 1fr);
    
    ${media.down.lg`
      grid-template-columns: repeat(3, 1fr);
    `}
    
    ${media.down.md`
      grid-template-columns: repeat(2, 1fr);
    `}
    
    ${media.down.sm`
      grid-template-columns: 1fr;
    `}
  }
  
  /* Utility classes */
  .text-center {
    text-align: center;
  }
  
  .mb-1 {
    margin-bottom: var(--spacing-sm);
  }
  
  .mb-2 {
    margin-bottom: var(--spacing-md);
  }
  
  .mb-3 {
    margin-bottom: var(--spacing-lg);
  }
  
  .mb-4 {
    margin-bottom: var(--spacing-xl);
  }
  
  .mb-5 {
    margin-bottom: var(--spacing-xxl);
  }
  
  .mt-1 {
    margin-top: var(--spacing-sm);
  }
  
  .mt-2 {
    margin-top: var(--spacing-md);
  }
  
  .mt-3 {
    margin-top: var(--spacing-lg);
  }
  
  .mt-4 {
    margin-top: var(--spacing-xl);
  }
  
  .mt-5 {
    margin-top: var(--spacing-xxl);
  }
  
  /* Responsive visibility classes */
  .hide-on-mobile {
    ${media.down.sm`
      display: none !important;
    `}
  }
  
  .hide-on-tablet {
    ${media.down.md`
      display: none !important;
    `}
  }
  
  .show-on-mobile {
    display: none !important;
    
    ${media.down.sm`
      display: block !important;
    `}
  }
  
  .show-on-tablet {
    display: none !important;
    
    ${media.down.md`
      display: block !important;
    `}
  }
  
  /* Dark mode specific styles */
  .card, .service-card, .project-card, .team-card, .blog-card {
    background-color: var(--card-bg);
    border: 1px solid var(--border-color);
    transition: background-color 0.3s ease, border-color 0.3s ease;
  }

  input, textarea, select {
    background-color: var(--input-bg);
    color: var(--input-text);
    border: 1px solid var(--border-color);
    transition: background-color 0.3s ease, color 0.3s ease, border-color 0.3s ease;
    
    &::placeholder {
      color: var(--gray);
    }
  }

  /* Theme toggle button styles */
  .theme-toggle {
    background: transparent;
    border: none;
    color: var(--text-color);
    cursor: pointer;
    padding: 0.5rem;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.2rem;
    transition: color 0.3s ease;
    
    &:hover {
      color: var(--primary);
      background: transparent;
      transform: none;
    }
  }
`;

export default GlobalStyles;
